import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import styled, { keyframes } from "styled-components";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import MinMax from 'dayjs/plugin/minMax';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(MinMax);
dayjs.extend(timezone);
import { scrollbarWidth } from './utils';
import NotificationPopup from './NotificationPopup';
import Button from './Button';
export default React.memo(function TimeLine(props) {
    var _a;
    const tr = (_a = props.t) !== null && _a !== void 0 ? _a : (v => v);
    const containerRef = React.useRef(null);
    const timeLineRef = React.useRef(null);
    const currentItemRef = React.useRef();
    const tmpItemRef = React.useRef();
    const containerWidthRef = React.useRef(0);
    const itemWidthRef = React.useRef(0);
    const scrollBarThickness = React.useRef(0);
    const [currentValue, setCurrentValue] = React.useState(props.value);
    const [currentRange, setCurrentRange] = React.useState([]);
    const [needConfirm, setNeedConfirm] = React.useState(false);
    const [_windowSize, _setWindowSize] = React.useState(0);
    let isMouseDown = false;
    let mouseMoveStartX = 0;
    let timeLineScrollLeft = 0;
    let timerId;
    React.useEffect(() => {
        scrollBarThickness.current = scrollbarWidth();
        const resizeHandler = () => _setWindowSize(window.innerWidth);
        window.addEventListener('click', onOuterClick);
        window.addEventListener('resize', resizeHandler);
        window.addEventListener('wheel', onMouseWheel);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('click', onOuterClick);
            window.removeEventListener('resize', resizeHandler);
            window.removeEventListener('wheel', onMouseWheel);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, []);
    React.useEffect(() => {
        reSetTimeLine();
        scrollToCurrentItem('smooth');
    }, [_windowSize]);
    React.useEffect(() => {
        drawCurrentRange(props.value);
    }, [props.mode]);
    React.useEffect(() => {
        reSetTimeLine();
        scrollToCurrentItem();
    }, [currentRange]);
    React.useEffect(() => {
        var _a;
        if (currentValue === props.value)
            return;
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, currentValue);
    }, [currentValue]);
    const drawCurrentRange = React.useCallback((currValue) => {
        let itemsResult = [];
        switch (props.mode) {
            case 'year':
                itemsResult = drawYears(currValue);
                break;
            case 'month':
                itemsResult = drawMonths(currValue);
                break;
            case 'day':
                itemsResult = drawDays(currValue);
                break;
            case 'hour':
                itemsResult = drawHours(currValue);
                break;
            case 'minute':
                itemsResult = drawMinutes(currValue);
                break;
            case 'second':
                itemsResult = drawSeconds(currValue);
                break;
        }
        setCurrentRange(itemsResult);
    }, [props.mode]);
    const getDate = React.useCallback((i, currentValue) => {
        return dayjs.utc(currentValue).add(i, props.mode);
    }, [props.mode]);
    const drawYears = (currentValue) => {
        const itemsResult = [];
        const minYear = props.minYear || 1200;
        const maxYear = props.maxYear || 2400;
        const currentYear = dayjs.utc(currentValue).year();
        const onTheSides = 100;
        const rangeStart = (minYear - onTheSides) - currentYear;
        const rangeEnd = (maxYear + onTheSides) - currentYear;
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const date = getDate(i, currentValue);
            const dateIso = date.toISOString();
            const dateYear = date.year();
            const classList = ['item'];
            if (dateYear < minYear || dateYear > maxYear) {
                classList.push('disabled');
            }
            if (dateIso === currentValue) {
                classList.push('current');
            }
            itemsResult.push({
                textContent: `${currentYear + i}`,
                dateIso,
                status: classList.join(' ')
            });
        }
        return itemsResult;
    };
    const drawMonths = (currentValue) => {
        const itemsResult = [];
        const currentDate = dayjs.utc(currentValue);
        const currentYear = currentDate.year();
        const rangeStart = -300;
        const rangeEnd = 300;
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const date = getDate(i, currentValue);
            const dateIso = date.toISOString();
            const year = +date.format('YYYY');
            const month = +date.format('M');
            const badge = month === 1 ? date.format('YYYY') : undefined;
            const classList = ['item'];
            if (dateIso === currentValue) {
                classList.push('current');
            }
            if (currentYear !== year) {
                classList.push('out-range');
            }
            itemsResult.push({
                textContent: `${month}`,
                dateIso,
                status: classList.join(' '),
                badge
            });
        }
        return itemsResult;
    };
    const drawDays = (currentValue) => {
        const itemsResult = [];
        const months = [
            "base.months.january.nom",
            "base.months.february.nom",
            "base.months.march.nom",
            "base.months.april.nom",
            "base.months.may.nom",
            "base.months.june.nom",
            "base.months.july.nom",
            "base.months.august.nom",
            "base.months.september.nom",
            "base.months.october.nom",
            "base.months.november.nom",
            "base.months.december.nom"
        ];
        const currentDate = dayjs.utc(currentValue);
        const currentMonth = +currentDate.format('M');
        const rangeStart = -600;
        const rangeEnd = 600;
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const date = getDate(i, currentValue);
            const dateIso = date.toISOString();
            const day = +date.format('D');
            const month = +date.format('M');
            const monthName = date.format('MMMM');
            const badge = day === 1 ? (props.t ? tr(months[date.month()]) : monthName) : undefined;
            const classList = ['item'];
            if (dateIso === currentValue) {
                classList.push('current');
            }
            if (currentMonth !== month) {
                classList.push('out-range');
            }
            itemsResult.push({
                textContent: `${day}`,
                dateIso,
                status: classList.join(' '),
                badge
            });
        }
        return itemsResult;
    };
    const drawHours = (currentValue) => {
        const itemsResult = [];
        const currentDate = dayjs.utc(currentValue);
        const currentDay = currentDate.date();
        const rangeStart = -600;
        const rangeEnd = 600;
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const date = getDate(i, currentValue);
            const dateIso = date.toISOString();
            const day = date.date();
            const hour = date.hour();
            const badge = hour === 0 ? date.toDate().toLocaleDateString() : undefined;
            const classList = ['item'];
            if (dateIso === currentValue) {
                classList.push('current');
            }
            if (currentDay !== day) {
                classList.push('out-range');
            }
            itemsResult.push({
                textContent: `${hour}`,
                dateIso,
                status: classList.join(' '),
                badge
            });
        }
        return itemsResult;
    };
    const drawMinutes = (currentValue) => {
        const itemsResult = [];
        const currentDate = dayjs.utc(currentValue);
        const currentHour = currentDate.hour();
        const rangeStart = -600;
        const rangeEnd = 600;
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const date = getDate(i, currentValue);
            const dateIso = date.toISOString();
            const hour = date.format('HH');
            const minute = date.minute();
            const badge = minute === 0 ? `${hour}` : undefined;
            const classList = ['item'];
            if (dateIso === currentValue) {
                classList.push('current');
            }
            if (currentHour !== +hour) {
                classList.push('out-range');
            }
            itemsResult.push({
                textContent: `${minute}`,
                dateIso,
                status: classList.join(' '),
                badge
            });
        }
        return itemsResult;
    };
    const drawSeconds = (currentValue) => {
        const itemsResult = [];
        const currentDate = dayjs.utc(currentValue);
        const currentMinute = currentDate.minute();
        const rangeStart = -600;
        const rangeEnd = 600;
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const date = getDate(i, currentValue);
            const dateIso = date.toISOString();
            const hour = date.format('HH');
            const minute = date.format('mm');
            const second = date.second();
            const badge = second === 0 ? `${hour}:${minute}` : undefined;
            const classList = ['item'];
            if (dateIso === currentValue) {
                classList.push('current');
            }
            if (currentMinute !== +minute) {
                classList.push('out-range');
            }
            itemsResult.push({
                textContent: `${second}`,
                dateIso,
                status: classList.join(' '),
                badge
            });
        }
        return itemsResult;
    };
    const onTimeLineClick = (evt) => {
        const item = evt.target;
        if (!item.classList.contains('item'))
            return;
        tmpItemRef.current = item;
        if (!true) {
            setNeedConfirm(true);
        }
        else {
            applySelectedValue();
        }
    };
    const moveToItem = (dir) => {
        var _a;
        tmpItemRef.current = (_a = currentItemRef.current) === null || _a === void 0 ? void 0 : _a[dir > 0 ? 'nextElementSibling' : 'previousElementSibling'];
        if (!true) {
            setNeedConfirm(true);
        }
        else {
            applySelectedValue();
        }
    };
    const onMouseWheel = (evt) => {
        const dir = Math.sign(evt.deltaY);
        moveToItem(dir);
    };
    const onKeyDown = (evt) => {
        var _a;
        if (evt.key === 'Escape') {
            (_a = props.onHide) === null || _a === void 0 ? void 0 : _a.call(props);
        }
        else if (evt.key === 'ArrowLeft') {
            moveToItem(-1);
        }
        else if (evt.key === 'ArrowRight') {
            moveToItem(1);
        }
    };
    const reSetTimeLine = () => {
        var _a, _b, _c;
        currentItemRef.current = (_a = timeLineRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.current');
        containerWidthRef.current = (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width;
        itemWidthRef.current = (_c = currentItemRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().width;
    };
    const scrollToCurrentItem = (smooth) => {
        var _a, _b;
        const offsetLeft = ((_a = currentItemRef.current) === null || _a === void 0 ? void 0 : _a.offsetLeft) - (containerWidthRef.current / 2) + (itemWidthRef.current / 2);
        if (smooth === 'smooth') {
            (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.scroll({ left: offsetLeft, behavior: 'smooth' });
        }
        else {
            containerRef.current.scrollLeft = offsetLeft;
        }
    };
    const smoothScroll = (scrolledContainer, toPosition) => {
        return new Promise((resolve, reject) => {
            const failed = setTimeout(() => {
                reject(scrolledContainer.scrollLeft);
            }, 2000);
            requestAnimationFrame(check);
            scrolledContainer.scroll({ left: toPosition, behavior: 'smooth' });
            function check() {
                if (scrolledContainer.scrollLeft === Math.trunc(toPosition)) {
                    clearTimeout(failed);
                    return resolve(scrolledContainer.scrollLeft);
                }
                else {
                    requestAnimationFrame(check);
                }
            }
        });
    };
    const applySelectedValue = async () => {
        var _a;
        (_a = currentItemRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove('current');
        currentItemRef.current = tmpItemRef.current;
        currentItemRef.current.classList.add('current');
        const scrollTo = (currentItemRef.current.offsetLeft - (containerWidthRef.current / 2) + (itemWidthRef.current / 2));
        await smoothScroll(containerRef.current, scrollTo);
        clearTimeout(timerId);
        timerId = setTimeout(async () => {
            const itemDate = currentItemRef.current.getAttribute('data-date');
            itemDate && setCurrentValue(itemDate);
        }, 0);
    };
    const onMouseDown = (evt) => {
        var _a, _b, _c;
        isMouseDown = true;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add('active');
        mouseMoveStartX = evt.nativeEvent.pageX - ((_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.offsetLeft);
        timeLineScrollLeft = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.scrollLeft;
    };
    const onMouseLeave = (evt) => {
        var _a;
        evt.preventDefault();
        evt.nativeEvent.stopImmediatePropagation();
        isMouseDown = false;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
    };
    const onMouseMove = (evt) => {
        var _a;
        if (!isMouseDown)
            return;
        evt.preventDefault();
        const x = evt.nativeEvent.pageX - ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetLeft);
        const walk = (x - mouseMoveStartX) * 3;
        containerRef.current.scrollLeft = timeLineScrollLeft - walk;
    };
    const onOuterClick = (ev) => {
        var _a;
        if (!containerRef.current.contains(ev.target)) {
            (_a = props.onHide) === null || _a === void 0 ? void 0 : _a.call(props);
        }
    };
    const createItem = ({ content, dateIso, badge }) => {
        const itemElement = document.createElement('span');
        itemElement.textContent = content;
        if (badge) {
            const badgeElement = document.createElement('ins');
            badgeElement.textContent = badge;
            badgeElement.className = 'badge';
            itemElement.appendChild(badgeElement);
        }
        itemElement.setAttribute('date', dateIso);
        itemElement.classList.add('item');
        return itemElement;
    };
    return (_jsxs(_Fragment, { children: [_jsx(Wrapper, { className: `time-line-wrapper${!props.mode ? ' hidden' : ''}`, children: _jsx(Container, { className: "time-line-container", ref: containerRef, onMouseDown: onMouseDown, onMouseUp: onMouseLeave, onMouseLeave: onMouseLeave, onMouseMove: onMouseMove, children: _jsx(TimeLineContainer, { ref: timeLineRef, className: "time-line", onClick: onTimeLineClick, scrollBar: scrollBarThickness.current, children: currentRange.map((item, idx) => {
                            return (_jsxs(Item, { className: item.status, "data-idx": idx, "data-date": item.dateIso, children: [item.badge && _jsx("ins", { className: "badge", children: item.badge }), item.textContent] }, item.dateIso));
                        }) }) }) }), needConfirm &&
                _jsx(NotificationPopup, { type: "warning", size: "small", title: "\u041F\u043E\u0441\u0442\u0440\u043E\u0435\u043D\u0438\u044F \u0431\u0443\u0434\u0443\u0442 \u0443\u0434\u0430\u043B\u0435\u043D\u044B", content: _jsx("span", { children: "Content" }), buttons: _jsxs(_Fragment, { children: [_jsx(Button, { size: "medium", onClick: () => {
                                    var _a, _b;
                                    setNeedConfirm(false);
                                    (_a = props.openInTemporaryMap) === null || _a === void 0 ? void 0 : _a.call(props, (_b = tmpItemRef.current) === null || _b === void 0 ? void 0 : _b.getAttribute('data-date'));
                                }, children: "Открыть во временной карте" }), _jsx(Button, { size: "medium", color: "transparent", onClick: () => {
                                    setNeedConfirm(false);
                                    applySelectedValue();
                                }, children: "Изменить данные" })] }) })] }));
});
const Wrapper = styled.div `
  position: absolute;
  box-sizing: border-box;
  left: 0;
  width: 100%;
  height: 10rem;
  bottom: 0;

  &.hidden {
    visibility: hidden;
    pointer-events: none;
  }
`;
const fadeIn = keyframes `
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const Container = styled.div `
  position: absolute;
  box-sizing: border-box;
  left: 0;
  width: 100%;
  height: 100%;
  user-select: none;
  overflow-x: scroll;

  /* background-color: #126d7a; */

  &.active {
    cursor: move;
    cursor: grabbing;
    cursor: -moz-grabbing;    
    cursor: -webkit-grabbing;
  }

  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
    border: none;
  }

  &.active {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }
`;
const TimeLineContainer = styled.div `
  display: flex;
  position: absolute;
  left: 0;
  top: calc(50% + ${p => p.scrollBar / 2}px);
  width: 100%;
  transform: translateY(-50%);
`;
const Item = styled.span `
  display: inline-flex;
  position: relative;
  box-sizing: border-box;
  height: 1.4rem;
  min-width: 3rem;
  padding: 0 0.5rem;
  justify-content: center;
  align-items: center;

  color: var(--text-primary);
  font-size: 0.875rem;

  border-radius: 0.625rem;
  cursor: pointer;

  /* &:not(.disabled):not(.out-range) {
    opacity: 0;
    animation: ${fadeIn} 0.8s ease both;
  } */

  &:hover {
    opacity: 0.6;
  }

  &.current {
    background-color: var(--accent-blue);
    color: #fff;
  }

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  /* &.out-range:not(.current) {
    opacity: 0.5;

    &:hover {
      opacity: 0.3;
    }
  } */

  & > .badge {
    position: absolute;
    bottom: 1.8rem;
    left: 1.5rem;
    text-decoration: none;
    font-size: 0.8rem;
    color: var(--text-secondary);
  }
`;
const Focus = styled.div `
  position: absolute;
  box-sizing: border-box;
  width: 3rem;
  height: 1.4rem;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  padding: 0 0.5rem;
  border-radius: 0.625rem;
  pointer-events: none;

  z-index: 3;

  border: 1px solid var(--accent-blue);
  opacity: 0.87;
`;
